import { useMemo, useEffect, useState } from 'react'
import { FC } from 'react'
import * as styles from 'components/features/socialFeed/SocialProfileInfo/SocialProfileInfo.styl'
import { Field } from 'redux-form'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import SvgIcon from 'components/atoms/SvgIcon'
import PencilIcon from 'icons/pencil.svg'
import { FIELD_USERNAME_PROPS } from 'constants/fields'
import { FeedUser } from 'interfaces/feed'
import { UserImage } from 'interfaces/userImage'
import { AvatarField } from 'components/features/socialFeed/AvatarField/AvatarField'
import { AvatarEditable } from 'components/features/socialFeed/AvatarEditable/AvatarEdiatble'
import cn from 'classnames'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'

const USERNAME_PROPS = {
  ...FIELD_USERNAME_PROPS,
  maxLength: 20,
  label: null,
  innerClassname: styles.textFieldInner,
}

export type SocialProfileInfoStateProps =
  & Pick<FeedUser, 'username'>
  & {
    avatar: UserImage | null
  }

export type SocialProfileInfoDispatchProps = {
  onEdit: () => void
}

export type SocialProfileInfoOwnProps = {
  readonly: boolean
}

export type SocialProfileInfoProps =
  & SocialProfileInfoStateProps
  & SocialProfileInfoDispatchProps
  & SocialProfileInfoOwnProps

const SocialProfileInfo: FC<SocialProfileInfoProps> = ({
  username,
  readonly,
  onEdit,
  avatar,
}) => {
  const readonlyChild = useMemo(() => (
    <div className={ styles.socialProfileInfo }>
      <div className={ styles.avatar }>
        <AvatarEditable
          actionTitle='Edit'
          onClick={ onEdit }
        />
      </div>
      <div className={ styles.usernameWrapper } onClick={ onEdit }>
        <div className={ styles.textFieldWrapper }>
          <div className={ styles.textFieldReadonly }>
            { username }
          </div>
          <div className={ styles.pencilIconWrapper }>
            <a className={ styles.pencilIcon } onClick={ onEdit }>
              <SvgIcon src={ PencilIcon } />
            </a>
          </div>
        </div>
      </div>
    </div>
  ), [onEdit, username])

  const avatarEditable = useMemo(() => {
    return (
      <Field
        name='avatar'
        component={ AvatarField }
        props={{
          username,
          preventLazyRender: true,
        }}
      />
    )
  }, [avatar || username])

  const isMobile = useStoreSelector(isMobileSelector)
  const needAutoFocus = !readonly && !isMobile

  const [fixAutoScroll, setFixAutoScroll] = useState(true)

  const textField = useMemo(() => (
    <div className={ cn(fixAutoScroll && styles.preventAutoScroll) }>
      <Field
        name='username'
        component={ TextFieldWrapped }
        readOnly={ readonly }
        props={{
          ...USERNAME_PROPS,
          autoFocus: needAutoFocus,
        }}
        forceAutoFocus={ needAutoFocus }
      />
    </div>
  ), [fixAutoScroll, needAutoFocus, readonly])

  useEffect(() => {
    if (fixAutoScroll) {
      setFixAutoScroll(false)
    }
  }, [fixAutoScroll])

  useEffect(() => {
    if (needAutoFocus) {
      return
    }

    if (document.activeElement && document.activeElement) {
      const blurElement = document.activeElement as HTMLInputElement
      blurElement && blurElement.blur()
    }
  }, [needAutoFocus])

  if (readonly) {
    return readonlyChild
  }

  return (
    <div className={ styles.socialProfileInfo }>
      { avatarEditable }
      <div className={ styles.usernameWrapper }>
        { textField }
      </div>
    </div>
  )
}

export default SocialProfileInfo
